import {useEffect, useState} from 'react'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {SelectList, SelectPopover, SelectTrigger} from '@/components/ui/select-atoms/SelectAtoms'
import dayjs from 'dayjs'
import {SelectProvider, useSelectStore} from '@ariakit/react'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {StyledInputTimeSelectItem} from '@/components/commons/input-time/style'

/**
 * Interface for the InputTime component props
 */
interface InputTimeProps {
    /** Input name attribute */
    name?: string
    /** Label text for the input */
    label?: string
    /** Placeholder text for the input */
    placeholder?: string
    /** Whether the component should take full width of its container */
    fullWidth?: boolean
    /** Handler called when the time value changes */
    onChange?: (value: string | null) => void
    /** Whether the input is disabled */
    disabled?: boolean
    /** Error message to display */
    errorMessage?: string
    /** Whether the field is optional */
    isOptional?: boolean
    /** The currently selected time value (in 24h format, e.g. "14:30") */
    value?: string
    /** The earliest time that can be selected (in 24h format, e.g. "09:00") */
    startTime?: string
    /** The latest time that can be selected (in 24h format, e.g. "18:00") */
    endTime?: string
    /** The interval between time options in minutes (default: 30) */
    interval?: number
    /** Whether to use 12h format (AM/PM) for display (default: false) */
    use12HourFormat?: boolean
    /** The available slots for the selected date */
    availableSlots?: string[]
    /** The warning slots for the selected date */
    warningSlots?: string[]
    /** Whether the time options are loading */
    isLoading?: boolean
}

/**
 * Time format constants
 */
const TIME_FORMAT_24H = 'HH:mm'
const TIME_FORMAT_12H = 'h:mm A'

/**
 * Validates if a string is in the format HH:MM (24-hour format) using dayjs
 */
const isValidTimeFormat = (time: string): boolean => {
    return dayjs(time, TIME_FORMAT_24H, true).isValid()
}

/**
 * Converts 24-hour format time to 12-hour format with AM/PM using dayjs
 */
const convertTo12HourFormat = (time: string): string => {
    if (!isValidTimeFormat(time)) return time

    return dayjs(time, TIME_FORMAT_24H).format(TIME_FORMAT_12H)
}

/**
 * Converts a time string to minutes since midnight using dayjs
 */
const timeToMinutes = (time: string): number => {
    if (!isValidTimeFormat(time)) return 0

    const timeObj = dayjs(time, TIME_FORMAT_24H)
    return timeObj.hour() * 60 + timeObj.minute()
}

/**
 * Converts minutes since midnight to a time string in HH:MM format using dayjs
 */
const minutesToTime = (minutes: number): string => {
    const date = dayjs().startOf('day').add(minutes, 'minute')
    return date.format(TIME_FORMAT_24H)
}

/**
 * Generates an array of time options between startTime and endTime with the specified interval
 */
export const generateTimeOptions = (startTime: string, endTime: string, interval: number): string[] => {
    // Validate inputs
    if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
        return []
    }

    // Ensure interval is positive
    const safeInterval = Math.max(1, interval)

    // Convert times to minutes for easier calculation
    const startMinutes = timeToMinutes(startTime)
    const endMinutes = timeToMinutes(endTime)

    // Handle case where startTime is after endTime
    if (startMinutes > endMinutes) {
        console.error('startTime cannot be after endTime in generateTimeOptions')
        return []
    }

    const timeOptions: string[] = []

    // Generate time options at specified intervals
    for (let minutes = startMinutes; minutes <= endMinutes; minutes += safeInterval) {
        timeOptions.push(minutesToTime(minutes))
    }

    // Ensure endTime is included if it falls on an interval boundary
    if (endMinutes % safeInterval == 0 && !timeOptions.includes(endTime)) {
        timeOptions.push(endTime)
    }

    return timeOptions
}
export const DEFAULT_AVAILABILITY_OPTIONS = generateTimeOptions('07:00', '20:00', 15)

/**
 * Validates if a time is within the allowed range using dayjs
 */
const isTimeInRange = (time: string, startTime: string, endTime: string): boolean => {
    if (!isValidTimeFormat(time) || !isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
        return false
    }

    const timeValue = dayjs(time, TIME_FORMAT_24H)
    const startValue = dayjs(startTime, TIME_FORMAT_24H)
    const endValue = dayjs(endTime, TIME_FORMAT_24H)

    return (
        timeValue.isAfter(startValue) ||
        (timeValue.isSame(startValue) && (timeValue.isBefore(endValue) || timeValue.isSame(endValue)))
    )
}

/**
 * InputTime component for selecting time values
 */
export const InputTime = ({
    name,
    placeholder,
    value,
    fullWidth,
    onChange,
    disabled,
    label,
    errorMessage,
    isOptional,
    startTime,
    endTime,
    interval = 30,
    availableSlots,
    warningSlots,
    isLoading = false,
    use12HourFormat = false
}: InputTimeProps) => {
    const {t} = useTranslation()
    const [inputValue, setInputValue] = useState('')

    // Generate time options based on props
    const timeOptions = startTime && endTime ? generateTimeOptions(startTime, endTime, interval) : []

    // Create select store for the dropdown
    const select = useSelectStore({
        value: value || '',
        setValue: newValue => {
            if (newValue) {
                setInputValue(newValue)
                onChange?.(newValue)
            }
        }
    })

    /**
     * Handles selection of a time from the dropdown
     */
    const handleTimeSelect = (time: string) => {
        setInputValue(time)
        onChange?.(time)
        select.hide()
    }

    // Update input value when the value prop changes
    useEffect(() => {
        if (value) {
            // Validate the value prop
            if (isValidTimeFormat(value) && startTime && endTime && isTimeInRange(value, startTime, endTime)) {
                setInputValue(value)
                select.setValue(value)
            } else {
                console.warn('Invalid time value provided to InputTime:', value)
                setInputValue('')
                select.setValue('')
            }
        } else {
            setInputValue('')
            select.setValue('')
        }
    }, [value, startTime, endTime, select, t])

    // Display value in the selected format
    const displayValue = use12HourFormat && inputValue ? convertTo12HourFormat(inputValue) : inputValue

    // Format time for display in the dropdown
    const formatTimeForDisplay = (time: string): string => {
        return use12HourFormat ? convertTo12HourFormat(time) : time
    }

    return (
        <Flexbox direction="column" gap={1.5} fullWidth={fullWidth}>
            {label && (
                <Label htmlFor={name}>
                    {label} {isOptional && <span className="description">{t('commons:optional')}</span>}
                </Label>
            )}
            <Flexbox direction="column" gap={2} fullWidth>
                <SelectProvider store={select}>
                    <SelectTrigger
                        placeholder={placeholder}
                        value={displayValue}
                        fullWidth
                        disabled={disabled}
                        hasError={!!errorMessage}
                        displayValue={() => displayValue}
                        isLoading={isLoading}
                    />
                    <SelectPopover gutter={4} style={{maxHeight: '200px'}} unmountOnHide>
                        <SelectList>
                            {timeOptions.map(time => (
                                <StyledInputTimeSelectItem
                                    key={time}
                                    value={time}
                                    onClick={() => handleTimeSelect(time)}
                                    disabled={availableSlots && !availableSlots.includes(time)}
                                    $isWarning={warningSlots && !warningSlots.includes(time)}
                                    hasItemCheckbox
                                >
                                    {formatTimeForDisplay(time)}
                                </StyledInputTimeSelectItem>
                            ))}
                        </SelectList>
                    </SelectPopover>
                </SelectProvider>
                {errorMessage && <InputHelpText error={errorMessage} />}
            </Flexbox>
        </Flexbox>
    )
}
